import { cn } from "@/utils/cn";
import {
  Badge,
  LoadingOverlay,
  Radio,
  RadioGroupProps,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconInfoCircle } from "@tabler/icons-react";

type DescriptiveRadioGroupProps = Omit<
  RadioGroupProps,
  "children" | "onChange"
> & {
  options: {
    title: string;
    zipcode: boolean;
    value: string;
    price: string;
    disabled?: boolean;
    optionKey?: string;
  }[];
  onChange: (value: any) => void;
  data: any;
  setData: (data: any) => void;
  isLoading: boolean;
  shippingStatus?: string;
  hideFreeShippingBadge?: boolean;
};

const DescriptiveRadioGroup = ({
  options = [],
  isLoading,
  shippingStatus,
  hideFreeShippingBadge = false,
  ...props
}: DescriptiveRadioGroupProps) => {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const handleCardClick = (optionValue: string, disabled?: boolean) => {
    if (!disabled && props.onChange) {
      props.onChange(optionValue);
    }
  };

  const badges = {
    oversize_calculated: {
      label: isMobile
        ? "UPS oversize rate"
        : "Large package — UPS oversize rate",
      tooltipText:
        "Exceeds Standard Ground package limits, UPS Large Package surcharge applies.",
    },
    standard: {
      label: "Standard carrier rate",
      tooltipText:
        "Shipping rate based on standard carrier pricing for this order.",
    },
    multiple_calculated: {
      label: "Additional shipping fees apply",
      tooltipText:
        "Multiple items in this order have shipping costs. Shipping is calculated from the pricing table.",
    },
  };

  // Badge is decided by the status only (2026-07-18 rule, 10-19 trimmed):
  // free shipping ended 10-04, so the "$0 → Ships Free!" badge is gone.
  //   • known informational status    → that status's badge (e.g. "standard")
  //   • anything else (real price)    → NO badge — the price speaks for itself
  const badgeKeyForOption = (): keyof typeof badges | null => {
    if (shippingStatus && shippingStatus in badges) {
      return shippingStatus as keyof typeof badges;
    }
    return null;
  };

  return (
    <Radio.Group {...props} className="relative flex flex-col gap-2">
      <LoadingOverlay overlayProps={{ blur: 2 }} visible={isLoading} />
      {options.map(option => {
        if (option.disabled) {
          return null;
        }
        const optionBadgeKey = badgeKeyForOption();
        const badgeNode =
          option.optionKey?.includes("ups-ground") &&
          !hideFreeShippingBadge &&
          optionBadgeKey ? (
            <Tooltip
              label={badges[optionBadgeKey].tooltipText}
              multiline
              w={240}
              withArrow
              position="top-end"
              arrowOffset={16}
              disabled={!badges[optionBadgeKey].tooltipText}
              events={{ hover: true, focus: false, touch: true }}
            >
              <Badge
                color="#e8f7f7"
                onClick={e => {
                  // Mobile: tapping the badge opens the tooltip instead of
                  // toggling the option selection.
                  if (badges[optionBadgeKey].tooltipText) {
                    e.stopPropagation();
                  }
                }}
                rightSection={
                  badges[optionBadgeKey].tooltipText ? (
                    <IconInfoCircle size={14} />
                  ) : undefined
                }
                leftSection={
                  badges[optionBadgeKey].tooltipText ? undefined : (
                    <IconCheck size={14} />
                  )
                }
                px={8}
                style={
                  badges[optionBadgeKey].tooltipText
                    ? {
                        cursor: "help",
                        color: "#191919",
                        flexShrink: 1,
                        textTransform: "none",
                        fontSize: "15px",
                      }
                    : {
                        pointerEvents: "none",
                        color: "#191919",
                        flexShrink: 1,
                        textTransform: "none",
                        fontSize: "15px",
                      }
                }
              >
                {badges[optionBadgeKey].label}
              </Badge>
            </Tooltip>
          ) : null;
        return (
          <div
            key={option.title}
            className={cn(
              "mb-2 flex min-h-[48px] cursor-pointer items-center justify-between rounded-[4px] border border-solid border-[#00000014] bg-white p-3 transition-all hover:bg-[#00101004]",
              option.value === props.value && "border-[#4AA0A0]"
            )}
            onClick={() => handleCardClick(option.value, option.disabled)}
          >
            <div className="flex items-center gap-3">
              <Radio
                value={option.value}
                icon={IconCheck}
                color="#4AA0A0"
                disabled={option.disabled}
                className={`flex h-full items-center`}
                styles={{
                  radio: {
                    aspectRatio: "1/1",
                  },
                  body: {
                    display: "flex",
                    alignItems: "center",
                  },
                  label: {
                    fontSize: "16px",
                    fontWeight: 600,
                    flexShrink: 0,
                  },
                }}
              />
              <div className="flex flex-col gap-1.5">
                <p className="text-[18px] font-bold leading-none text-[#191919]">
                  {option.title}
                </p>
                {/* Mobile: badge sits on its own row below the title */}
                <div className="sm:hidden">{badgeNode}</div>
              </div>
            </div>
            {/* Desktop: badge stays inline between title and price */}
            <div className="hidden sm:block">{badgeNode}</div>
            <p className="text-md ml-2 shrink-0 font-bold leading-6 text-[#191919] md:ml-0">
              {/* 10-12: while rates load, the previous (pre-address grid) price
                  showed through the blur — e.g. $15.95. Hide it until live rates land. */}
              {isLoading ? "" : option.price}
            </p>
          </div>
        );
      })}
    </Radio.Group>
  );
};

export default DescriptiveRadioGroup;
